<i18n src="@/locales/modules/RequestTypeTranslate.json"></i18n>

<template>
	<div id="request-types-management" class="data-list-container">
		<div class="dropdown-button-container flex justify-end mt-16 md:hidden lg:hidden xl:hidden">
			<vs-input @input="mixinHandleSearch" size="large" class="input-search mr-3" icon="search" :placeholder="$t('Actions.Search')" v-model="searchText" />

			<vs-dropdown vs-custom-content vs-trigger-click v-if="mixinIsCanCreateRequestType">
				<vs-button class="btn-drop" type="filled" icon="expand_more"></vs-button>
				<vs-dropdown-menu>
					<vs-dropdown-item>
						<vs-button class="w-48 font-bold" type="filled" @click="addNewData">{{ $t("Actions.AddNew") }}</vs-button>
					</vs-dropdown-item>
				</vs-dropdown-menu>
			</vs-dropdown>
		</div>

		<vs-table ref="table" class="stack-table" :sst="true" :data="requestTypes" :max-items="limitPerPage" :noDataText="$t('Notify.NoDataAvailable')" @sort="mixinHandleSort">
			<div slot="header" class="flex flex-grow justify-end">
				<vs-input @input="mixinHandleSearch" size="large" class="input-search mr-3 md:block lg:block xl:block hidden" icon="search" :placeholder="$t('Actions.Search')" v-model="searchText" />
				<div class="md:flex lg:flex xl:flex hidden" v-if="mixinIsCanCreateRequestType">
					<vs-button class="w-30 font-bold" type="filled" @click="addNewData">{{ $t("Actions.AddNew") }}</vs-button>
				</div>
			</div>

			<template slot="thead">
				<vs-th sort-key="name">{{ $t("Name") }}</vs-th>
				<vs-th sort-key="disable">{{ $t("Status") }}</vs-th>
				<vs-th sort-key="type">{{ $t("Type") }}</vs-th>
				<vs-th sort-key="created_at">{{ $t("CreatedDate") }}</vs-th>
				<vs-th class="no-sorter"></vs-th>
			</template>

			<template slot-scope="{ data }">
				<tbody>
					<vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
						<vs-td :data-header="$t('Name')">
							<p class="font-medium">{{ tr.name }}</p>
						</vs-td>

						<vs-td :data-header="$t('Status')">
							<p :class="['font-medium font-bold max-w-xs', { 'text-solid-green-1': !tr.disable, 'text-solid-red': tr.disable }]">{{ $t($options.filters.filterStatus(tr.disable)) }}</p>
						</vs-td>
						<vs-td :data-header="$t('Type')">
							<p class="font-medium max-w-xs">{{ $t($options.filters.filterType(tr.type, mappedRequestTypes)) }}</p>
						</vs-td>

						<vs-td :data-header="$t('CreatedDate')">
							<p class="font-medium">{{ tr.created_at | dateTime(userDateTimeFormat) }}</p>
						</vs-td>

						<vs-td class="whitespace-no-wrap text-right">
							<request-type-context-menu
								:requestType="tr"
								@onClickEdit="editData"
								@onClickDelete="showDeleteConfirm"
								@onClickToggleStatus="handleUpdateRequestTypeStatus"
								@onClickSetting="
									id =>
										$router &&
										$router.history.push({
											params: { id: tr.id },
											name: $constant.PAGES_NAME.PAGE_CBS_REQUEST_TYPES_CONFIG_NAME
										})
								"
							/>
						</vs-td>
					</vs-tr>
				</tbody>
			</template>
		</vs-table>

		<CbsPagination class="mt-3" :total="totalPages" v-model="currentPage" />

		<request-type-dialog
			v-model="requestTypeData"
			v-if="isShowRequestTypeDialog"
			@closeDialog="toggleRequestTypeDialog"
			:isDialogActive="isShowRequestTypeDialog"
			@onSubmit="handleCreateOrUpdateRequestType"
		/>
	</div>
</template>

<script>
import _ from "lodash";
import { showConfirm, withBasicQueries, getUserPermissions, requestTypePermissions } from "@/mixins/mixins.js";
import { mapState } from "vuex";
import moduleRequestType from "@/store/request-type/moduleRequestType";

import RequestTypeDialog from "./RequestTypeDialog";
import RequestTypeContextMenu from "./RequestTypeContextMenu";

const initRequestTypeData = {
	id: null,
	name: "",
	typeId: null,
	attachFile: null,
	allowedPeople: [],
	generate_code: "",
	schedule_generate_code: 1,
	isPrivate: false
};

export default {
	mixins: [showConfirm, withBasicQueries, getUserPermissions(requestTypePermissions)],
	components: {
		RequestTypeDialog,
		RequestTypeContextMenu
	},
	data() {
		return {
			requestTypeData: null,
			isShowRequestTypeDialog: false
		};
	},
	computed: {
		...mapState("requestTypeManagement", ["limitPerPage", "requestTypes", "total"]),
		totalPages() {
			return Math.ceil(this.total / this.limitPerPage);
		},
		mappedRequestTypes() {
			return {
				[this.$constant.REQUEST_TYPE_GENERAL]: "GeneralRequest",
				[this.$constant.REQUEST_TYPE_DEVICE]: "DeviceRequest"
			};
		}
	},
	watch: {
		currentPage() {
			this.getRequestTypes();
		},
		"$route.query"(newVal, oldVal) {
			const newValClone = Object.assign({}, newVal);
			const oldValClone = Object.assign({}, oldVal);
			if (!newValClone.id) {
				delete oldValClone.id;
			}
			if (!_.isEqual(newValClone, oldValClone)) {
				this.handleFetchDataByQuery();
			}
		}
	},
	methods: {
		async getRequestTypes() {
			try {
				this.$vs.loading();

				let queryOptions = {
					list_all: true,
					list_all_controls: true,
					orderBy: this.orderBy,
					page: this.currentPage,
					limit: this.limitPerPage
				};

				if (this.searchText) {
					queryOptions.keyword = this.searchText;
				}

				// Fetch requestTypesList
				await this.$store.dispatch("requestTypeManagement/getRequestTypes", queryOptions);
				if (this.$refs.table && this.searchText) {
					this.$refs.table.currentx = 1;
				}
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$nextTick(() => this.$vs.loading.close());
			}
		},
		addNewData() {
			this.requestTypeData = { ...initRequestTypeData };
			this.toggleRequestTypeDialog(true);
		},
		async editData(data) {
			try {
				this.$vs.loading();
				const response = await this.$store.dispatch("requestTypeManagement/getRequestTypeSimpleData", data.id);
				this.requestTypeData = {
					...response,
					typeId: response.type,
					isDisabled: response.disable,
					isPrivate: response.is_private,
					attachFile: response.document,
					allowedPeople: response.who_can_create || []
				};
				this.toggleRequestTypeDialog(true);
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$nextTick(() => this.$vs.loading.close());
			}
		},
		showDeleteConfirm(id) {
			this.mixinShowConfirm().then(async isAccept => {
				if (isAccept) {
					try {
						this.$vs.loading();
						await this.$store.dispatch("requestTypeManagement/deleteRequestType", id);
						this.$helper.notifySuccess({
							context: this,
							options: {
								text: this.$t("DeleteRequestTypeSuccessful")
							}
						});
					} catch (error) {
						this.$helper.notifyError({ context: this, error });
					} finally {
						this.$nextTick(() => this.$vs.loading.close());
					}
				}
			});
		},
		toggleRequestTypeDialog(val = false) {
			this.isShowRequestTypeDialog = val;
		},
		async handleCreateOrUpdateRequestType() {
			try {
				this.$vs.loading();
				const payload = {
					name: this.requestTypeData.name,
					type: this.requestTypeData.typeId,
					who_can_create: (this.requestTypeData.allowedPeople || []).map(item => ({ id: item.id, type: item.type })),
					template_id: _.get(this.requestTypeData, "attachFile.id") ? Number(_.get(this.requestTypeData, "attachFile.id")) : null,
					generate_code: this.requestTypeData.generate_code,
					schedule_generate_code: this.requestTypeData.schedule_generate_code,
					isPrivate: this.requestTypeData.isPrivate
				};

				if (this.requestTypeData.id) {
					await this.$store.dispatch("requestTypeManagement/updateRequestType", {
						...payload,
						id: this.requestTypeData.id,
						disable: this.requestTypeData.isDisabled
					});
				} else {
					const response = await this.$store.dispatch("requestTypeManagement/addRequestType", payload);
					this.$router.push({
						name: this.$constant.PAGES_NAME.PAGE_CBS_REQUEST_TYPES_CONFIG_NAME,
						params: { id: response.id }
					});
				}

				this.toggleRequestTypeDialog();
				this.$helper.notifySuccess({
					context: this,
					options: {
						text: this.$t(`${this.requestTypeData.id ? "UpdateRequestTypeSuccessful" : "CreateRequestTypeSuccessful"}`)
					}
				});
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$nextTick(() => this.$vs.loading.close());
			}
		},
		handleFetchDataByQuery() {
			this.getRequestTypes().then(() => {
				if (this.$route.query.id) {
					this.editData({ id: this.$route.query.id });
				}
			});
		},
		async handleUpdateRequestTypeStatus(requestType) {
			try {
				this.$vs.loading();
				await this.$store.dispatch("requestTypeManagement/updateRequestType", {
					id: requestType.id,
					type: requestType.type,
					name: requestType.name,
					disable: !requestType.disable
				});
				this.$helper.notifySuccess({
					context: this,
					options: {
						text: this.$t("UpdateRequestTypeSuccessful")
					}
				});
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$nextTick(() => this.$vs.loading.close());
			}
		}
	},
	filters: {
		filterType(type, mappedRequestTypes) {
			return mappedRequestTypes[type];
		},
		filterStatus(disable) {
			if (!disable) {
				return "Available";
			}

			if (disable) {
				return "NotAvailable";
			}
		}
	},
	created() {
		this.$store.registerModule("requestTypeManagement", moduleRequestType);
		this.handleFetchDataByQuery();
	},
	beforeDestroy() {
		this.$store.commit("requestTypeManagement/RESET_STATE");
		this.$store.unregisterModule("requestTypeManagement");
	}
};
</script>
